import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import ptBR from './pt-BR';
import en from './en';

type Lang = 'pt-BR' | 'en';
type Region = 'br' | 'us' | 'ca';

type I18nContextType = {
  lang: Lang;
  t: (key: string) => string;
  setLang: (lang: Lang) => void;
  region: Region;
  setRegion: (region: Region) => void;
};

const dictionaries: Record<Lang, Record<string, string>> = {
  'pt-BR': ptBR,
  en
};

const I18nContext = createContext<I18nContextType>({
  lang: 'en',
  t: (key) => key,
  setLang: () => {},
  region: 'us',
  setRegion: () => {}
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>('en');
  const [region, setRegion] = useState<Region>('us');

  useEffect(() => {
    const saved = localStorage.getItem('lang') as Lang | null;
    const savedRegion = localStorage.getItem('langRegion') as Region | null;
    if (saved === 'pt-BR' || saved === 'en') {
      setLang(saved);
      if (savedRegion === 'br' || savedRegion === 'us' || savedRegion === 'ca') {
        setRegion(savedRegion);
      } else {
        setRegion(saved === 'pt-BR' ? 'br' : 'us');
      }
    } else {
      const browserLang = navigator.language?.toLowerCase();
      if (browserLang === 'pt-br') {
        setLang('pt-BR');
        setRegion('br');
      } else {
        setLang('en');
        if (browserLang === 'en-ca') {
          setRegion('ca');
        } else if (browserLang === 'en-us') {
          setRegion('us');
        } else {
          setRegion('us');
        }
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem('langRegion', region);
  }, [region]);

  useEffect(() => {
    if (lang === 'pt-BR') {
      setRegion('br');
    }
  }, [lang]);

  const t = useMemo(() => {
    const dict = dictionaries[lang];
    return (key: string) => dict[key] ?? key;
  }, [lang]);

  const value = useMemo(() => ({ lang, t, setLang, region, setRegion }), [lang, t, region]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
